'use client';

import { useEffect, useState } from 'react';
import { ApiConfigration } from '@/hotdesk-ui/common-classes/api-configuration';
import { ListingService, MyListing } from '@/hotdesk-ui/services/listing.service';
import { useAppSelector } from '@/hotdesk-ui/hooks/hook';
import { RootState } from '@/hotdesk-ui/store/store';

/**
 * Workspace listings keyed by language + workspace. A map marker can be
 * reopened many times in one session and the set doesn't change while the
 * user is browsing, so the second open is instant.
 */
const cache = new Map<string, MyListing[]>();

export interface UseWorkspaceListingsResult {
    listings: MyListing[];
    loading: boolean;
}

/**
 * The listings inside one workspace — the same rows the explore grid shows
 * when the user filters by listings instead of workspaces.
 *
 * Explore's workspace mode returns one representative row per workspace
 * (`distinct=workspace`), so the siblings are never in the page payload and
 * have to be fetched per workspace. Only runs while `enabled`, so nothing is
 * requested until a marker's popup is actually open.
 */
export function useWorkspaceListings(
    workspaceId?: number,
    enabled = false
): UseWorkspaceListingsResult {
    const languageCode = useAppSelector(
        (state: RootState) => state.listing.defaultLanguage
    );
    const [listings, setListings] = useState<MyListing[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!enabled || !workspaceId) return;

        const key = `${languageCode ?? ''}:${workspaceId}`;
        const cached = cache.get(key);
        if (cached) {
            setListings(cached);
            return;
        }

        let cancelled = false;
        setLoading(true);
        (async () => {
            try {
                const apiConfig = await new ApiConfigration().getApiConfig(
                    languageCode
                );
                const res = await new ListingService(
                    apiConfig
                ).getallListings({ workspaceId, status: '1' });
                const results = (res?.results ?? []) as MyListing[];
                cache.set(key, results);
                if (!cancelled) setListings(results);
            } catch (err) {
                console.error('Workspace listings fetch failed', err);
                // Leave `listings` empty — the caller falls back to the
                // workspace card rather than showing an empty popup.
                if (!cancelled) setListings([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [enabled, workspaceId, languageCode]);

    return { listings, loading };
}
